'use client';

import { subscribeToBrowserClickCapture } from '@nextmar/snowplow-sdk';
import { createZeeloolSnowplowDataAdapters } from '@/reporting/integrations/snowplow-sdk/adapters';
import { captureBrowserAnalytics } from '@/reporting/reliability/browserAnalytics';
import { isInZeloolApp } from '@/utils/tools';
import { getZeeloolSnowplowClient } from '@/reporting/integrations/snowplow-sdk/client';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect } from 'react';

/** PageView 路由去重时忽略的内部登录参数。 */
const PAGE_VIEW_IGNORED_QUERY_PARAMS = ['showLogin', 'from', 'reason'] as const;

/** 最近一次已安排的 PageView 候选；accepted 后继续承担连续同 key 去重。 */
let lastScheduledPageView: PageViewCandidate | null = null;

/** 最近一次被 SDK 接受且仍是最新访问的 PageView 候选。 */
let lastAcceptedPageView: PageViewCandidate | null = null;

/** PageView 候选的规范化路由。 */
interface PageViewCandidate {
  routeKey: string;
}

/** 浏览器当前路由去重所需的最小快照。 */
interface BrowserRouteSnapshot {
  pathname: string;
  search: string;
}

/** 去除 search 中可能混入的 hash，避免锚点变化触发 PageView。 */
function removeHashFragment(search: string): string {
  return search.split('#')[0] ?? '';
}

/** 过滤内部登录参数，同时保留其他业务 query。 */
function normalizePageViewSearch(search: string): string {
  const cleanSearch = removeHashFragment(search);

  // 空查询无需创建 URLSearchParams。
  if (!cleanSearch) return '';

  const queryText = cleanSearch.startsWith('?') ? cleanSearch.slice(1) : cleanSearch;

  // 只有问号时仍视为无查询。
  if (!queryText) return '';

  const params = new URLSearchParams(queryText);

  // 始终走同一序列化分支，避免 ignored 参数出现前后的 %20/+ 编码差异。
  PAGE_VIEW_IGNORED_QUERY_PARAMS.forEach((paramName) => {
    params.delete(paramName);
  });

  // URLSearchParams 保留其他业务参数的顺序与重复值。
  return params.toString();
}

/** 基于同一时刻的 pathname 与 search 构建 PageView route key。 */
function buildPageViewRouteKey(
  fallbackPathname: string | null,
  snapshot: BrowserRouteSnapshot | null,
): string | null {
  const pathname = snapshot?.pathname || fallbackPathname;

  // pathname 尚未就绪时不安排 PageView。
  if (!pathname) return null;

  const normalizedSearch = normalizePageViewSearch(snapshot?.search ?? '');

  // 没有业务 query 时仅使用 pathname。
  if (!normalizedSearch) return pathname;

  return `${pathname}${normalizedSearch.startsWith('?') ? normalizedSearch : `?${normalizedSearch}`}`;
}

/** 读取同一次浏览器 location 的 pathname 与 search。 */
function getBrowserRouteSnapshot(): BrowserRouteSnapshot | null {
  // SSR 环境不访问浏览器全局。
  if (typeof window === 'undefined') return null;

  return {
    pathname: window.location.pathname,
    search: window.location.search,
  };
}

/** 完成一个 PageView ticket，并仅在 accepted:true 时记录成功。 */
function settlePageViewCandidate(candidate: PageViewCandidate, accepted: boolean): void {
  // 旧 Promise 结束时不能覆盖或清掉后续访问的候选状态。
  if (lastScheduledPageView !== candidate) return;

  // accepted:true 保留最新候选以去重连续事件。
  if (accepted) {
    lastAcceptedPageView = candidate;
  } else {
    // 失败只清理当前候选，使同 route 后续事件能够重试。
    lastScheduledPageView = null;
    lastAcceptedPageView = null;
  }
}

/** 在首屏和 Next 客户端路由变化时调用 SDK 原生 PageView 与自动点击能力。 */
export function useSnowplowSdkLifecycle(): void {
  const pathname = usePathname();

  /** 捕获当前规范化 route key；浏览器交文档队列，App 保留 SDK ticket。 */
  const trackCurrentPageView = useCallback((): void => {
    const inApp = isInZeloolApp();
    const client = inApp ? getZeeloolSnowplowClient() : null;

    // App 保留既有 SDK 入口；浏览器捕获不等待 singleton 配置或 Tracker ready。
    if (inApp && !client) return;

    const snapshot = getBrowserRouteSnapshot();

    // 三方回跳先完成账户身份同步；清理 token 的历史事件会重新捕获 PageView。
    // 必须在捕获前等待，否则队列会保留登录前的匿名身份快照。
    if (!inApp && new URLSearchParams(snapshot?.search).get('token')) return;

    const routeKey = buildPageViewRouteKey(pathname, snapshot);

    // pathname 未就绪时不安排 PageView。
    if (!routeKey) return;

    const latestRouteWasAccepted =
      lastAcceptedPageView === lastScheduledPageView && lastAcceptedPageView?.routeKey === routeKey;
    const latestRouteIsPending =
      lastScheduledPageView?.routeKey === routeKey && !latestRouteWasAccepted;

    // 连续同 key 无论仍待返回还是已经接受，都不能重复发送。
    if (latestRouteWasAccepted || latestRouteIsPending) return;

    const candidate: PageViewCandidate = {
      routeKey,
    };
    lastScheduledPageView = candidate;

    try {
      // 浏览器同步接纳即占用去重资格，队列交付由应用级编排器负责。
      if (!inApp) {
        settlePageViewCandidate(candidate, captureBrowserAnalytics({ kind: 'pageView' }));
        return;
      }

      const resultPromise = client!.trackPageView();

      // rejected Promise 必须消费；accepted:false 释放同 route 的后续重试资格。
      void resultPromise
        .then((result) => {
          settlePageViewCandidate(candidate, result.accepted === true);
        })
        .catch(() => {
          settlePageViewCandidate(candidate, false);
        });
    } catch {
      // 同步异常同样只释放当前 ticket，不影响页面渲染或后续重试。
      settlePageViewCandidate(candidate, false);
    }
  }, [pathname]);

  // 首屏和 usePathname 变化均检查当前 route key。
  useEffect(() => {
    trackCurrentPageView();
  }, [trackCurrentPageView]);

  // 复用 PathnameTracker 已派发的历史事件，不再次包装 history 方法。
  useEffect(() => {
    if (typeof window === 'undefined') return;

    /** 将历史事件转换为一次当前 route key 检查。 */
    const handleRouteChange = (): void => {
      trackCurrentPageView();
    };
    /** BFCache恢复是新访问，同一路由重新取得一次PageView资格。 */
    const handlePageShow = (event: PageTransitionEvent): void => {
      if (!event.persisted || isInZeloolApp()) return;
      lastScheduledPageView = null;
      lastAcceptedPageView = null;
      trackCurrentPageView();
    };

    window.addEventListener('popstate', handleRouteChange);
    window.addEventListener('zl-history-change', handleRouteChange);
    window.addEventListener('pageshow', handlePageShow);

    /** 卸载当前 Hook 时移除两个浏览器历史监听。 */
    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      window.removeEventListener('zl-history-change', handleRouteChange);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [trackCurrentPageView]);

  // 自动点击的 DOM 提取、监听与共享生命周期全部交给 SDK。
  useEffect(() => {
    try {
      // 浏览器从首次 effect 起复用 SDK 同一个共享监听，配置后不另装发送监听。
      if (!isInZeloolApp()) {
        return subscribeToBrowserClickCapture(
          createZeeloolSnowplowDataAdapters(),
          (properties, context) => {
            // SDK 已在点击发生时冻结上下文，必须原样传入同一捕获队列。
            captureBrowserAnalytics({ kind: 'webClick', properties }, { context });
          },
        );
      }

      // App 保留原有 SDK 自动点击及其 cleanup 合同。
      return getZeeloolSnowplowClient()?.startAutoClickTracking();
    } catch {
      // 自动点击启动异常不影响 PageView 与页面交互。
      return;
    }
  }, []);
}
